// routes/abtest.view.$id/components/TestSummary/index.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { TrendingUp, TrendingDown, Users, MousePointer, ShoppingCart, DollarSign, Eye, Activity, Calendar, Filter } from 'lucide-react';
import {selectTestMetrics,fetchTestMetricsapi,fetchTestSummary,selectDailyMetrics,selectConversionFunnel,selectRealtimeSummary,selectTestSummaryById,selectLoading,selectError } from '../../../store/metricsSlice';
import { selectLogInUsername } from '../../../../../common/authenticateDashboard/authSlice';

const TestSummaryWithMetrics = ({ testId }) => {
    const dispatch = useDispatch();
    const sessionUser = useSelector(selectLogInUsername);
    const cf_username = sessionUser?.split('-prod@')[0];
    const loading = useSelector(selectLoading);
    const error = useSelector(selectError);
    const [test, setTest] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [dateRange, setDateRange] = useState('7d');
    const [refreshing, setRefreshing] = useState(false);
    const [metricsLoaded, setMetricsLoaded] = useState(false);
     // Create refs to always have current values
    const cfUsernameRef = useRef(cf_username);
    const testIdRef = useRef(testId);

    // Get processed data
    const metrics = useSelector(selectTestMetrics)[testId] || [];
    const summary = useSelector(selectTestSummaryById(testId)) || [];
    const funnel = useSelector(selectConversionFunnel(testId));
    const dailyMetrics = useSelector(selectDailyMetrics(testId, dateRange === '7d' ? 7 : 30));
    const realtimeSummary = useSelector(selectRealtimeSummary) || {};

    console.log("-----------------------",cf_username,testId, metrics, "summary", summary, "funnel", funnel, "dailyMetrics", dailyMetrics);

    useEffect(() => {
        cfUsernameRef.current = cf_username;
        testIdRef.current = testId;
    }, [cf_username, testId]);

    // Stable loadMetrics function
    // const loadMetrics = useCallback(async () => {
    //     if (!cf_username || !testId || metricsLoaded) return;
    //     console.log("Loading metrics for test", cf_username, testId);
    //     setRefreshing(true);
    //     try {
    //         // Dispatch both metrics and summary actions
    //         await Promise.all([
    //             dispatch(fetchTestMetricsapi(cf_username, testId)),
    //             dispatch(fetchTestSummary(cf_username, testId))
    //         ]);
    //         setMetricsLoaded(true);
    //     } catch (error) {
    //         console.error('Error loading metrics:', error);
    //     } finally {
    //         setRefreshing(false);
    //     }
    // }, [cf_username, testId, metricsLoaded, dispatch]);

    // Load test data and check for existing metrics
    useEffect(() => {
        if (testId && cf_username) {
            // Get test data from store
            const testData = 
            {
                basicInfo: {
                    testName: "Sample Test",
                    testType: "ELEMENT"
                },
                metadata: {
                    status: "ACTIVE"
                }
            };
            setTest(testData);

            // Check if we already have metrics
            if (metrics && metrics.length > 0) {
                setMetricsLoaded(true);
            } else {
                // Only load metrics if we don't have them
                // loadMetrics();
            }
        }
    }, [testId, cf_username]);

    // Manual refresh function
    const handleRefreshMetrics = useCallback(async () => {
        // Use ref.current to get the latest values
        const currentUsername = cfUsernameRef.current;
        const currentTestId = testIdRef.current;
        
        console.log('Refresh clicked with:', currentUsername, currentTestId);
        
        if (!currentUsername || !currentTestId) {
            console.error('Missing required values:', { currentUsername, currentTestId });
            return;
        }

        setRefreshing(true);
        setMetricsLoaded(false);
        try {
            await Promise.all([
                dispatch(fetchTestMetricsapi({cf_username: currentUsername, testId: currentTestId})),
                // dispatch(fetchTestSummary({cf_username: currentUsername, testId: currentTestId}))
            ]);
            setMetricsLoaded(true);
        } catch (error) {
            console.error('Error refreshing metrics:', error);
        } finally {
            setRefreshing(false);
        }
    }, [dispatch]);

    // Show loading state only if no test data
    if (!test && !metricsLoaded) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                <Activity className="w-8 h-8 mb-3 animate-spin" />
                <p>Loading test summary...</p>
            </div>
        );
    }

    // Calculate improvement percentages
    const calculateImprovement = (control, variant) => {
        if (!control || control === 0) return 0;
        return ((variant - control) / control * 100).toFixed(1);
    };

    const formatTestType = (testType) => {
        const types = {
            pricing: 'Pricing Test',
            element: 'Element Position Test',
            discount: 'Discount Test',
            cart: 'Cart Test'
        };
        return types[testType] || testType;
    };

    // Get status badge styles
    const getStatusBadgeStyles = (status) => {
        const styles = {
            active: 'bg-emerald-500 text-white',
            paused: 'bg-amber-500 text-white',
            stopped: 'bg-red-500 text-white',
            completed: 'bg-violet-500 text-white',
            unknown: 'bg-gray-600 text-white'
        };
        return styles[status?.toLowerCase()] || styles.unknown;
    };

    console.log("Rendering TestSummaryWithMetrics for test", testId, test, metrics);

    const renderOverviewTab = () => (
        <div className="flex flex-col gap-8">
            {/* Test Info Header */}
            <div className="flex flex-col lg:flex-row justify-between items-start gap-5 p-6 bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-xl">
                <div className="flex-1">
                    <h2 className="text-2xl md:text-3xl font-bold mb-3">
                        {test?.basicInfo?.testName || `Test ${testId}`}
                    </h2>
                    <div className="flex flex-wrap gap-4 mb-4">
                        <span className="bg-white/20 px-3 py-1.5 rounded-full text-sm font-medium">
                            {formatTestType(test?.basicInfo?.testType || 'element')}
                        </span>
                        <span className={`px-3 py-1.5 rounded-full text-sm font-semibold capitalize ${getStatusBadgeStyles(test?.metadata?.status || 'active')}`}>
                            {test?.metadata?.status || 'Active'}
                        </span>
                        <span className="bg-white/20 px-3 py-1.5 rounded-full text-sm font-medium">
                            {test?.settings?.basic?.trafficSplit || 50}% Traffic Split
                        </span>
                    </div>
                    {test?.basicInfo?.hypothesis && (
                        <p className="text-base opacity-90 leading-relaxed">{test.basicInfo.hypothesis}</p>
                    )}
                </div>

                <div>
                    <button
                        onClick={handleRefreshMetrics}
                        className="flex items-center gap-2 px-5 py-3 bg-white/20 text-white rounded-lg font-medium hover:bg-white/30 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                        disabled={refreshing}
                    >
                        <Activity className={refreshing ? 'animate-spin' : ''} size={16} />
                        {refreshing ? 'Refreshing...' : 'Refresh Data'}
                    </button>
                </div>
            </div>

            {/* Real-time Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                <div className="flex items-center gap-4 p-6 bg-white border border-slate-200 rounded-xl hover:border-blue-500 hover:shadow-lg transition-all duration-200">
                    <div className="flex items-center justify-center w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex-shrink-0">
                        <Eye size={24} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-slate-600 uppercase tracking-wide mb-2">Total Views</h3>
                        <div className="text-3xl font-bold text-slate-900 mb-1">
                            {(funnel.control.views + funnel.variant.views).toLocaleString()}
                        </div>
                        <div className="text-sm text-slate-600">
                            C: {funnel.control.views} | V: {funnel.variant.views}
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-4 p-6 bg-white border border-slate-200 rounded-xl hover:border-blue-500 hover:shadow-lg transition-all duration-200">
                    <div className="flex items-center justify-center w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex-shrink-0">
                        <MousePointer size={24} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-slate-600 uppercase tracking-wide mb-2">Button Clicks</h3>
                        <div className="text-3xl font-bold text-slate-900 mb-1">
                            {(funnel.control.button_clicks + funnel.variant.button_clicks).toLocaleString()}
                        </div>
                        <div className="text-sm text-slate-600">
                            C: {funnel.control.button_clicks} | V: {funnel.variant.button_clicks}
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-4 p-6 bg-white border border-slate-200 rounded-xl hover:border-blue-500 hover:shadow-lg transition-all duration-200">
                    <div className="flex items-center justify-center w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex-shrink-0">
                        <TrendingUp size={24} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-slate-600 uppercase tracking-wide mb-2">Click Rate</h3>
                        <div className="text-3xl font-bold text-slate-900 mb-1">
                            {funnel.variant.click_rate}%
                        </div>
                        <div className={`text-xs font-semibold mt-1 ${parseFloat(funnel.variant.click_rate) > parseFloat(funnel.control.click_rate)
                            ? 'text-emerald-600' : 'text-red-600'
                            }`}>
                            {calculateImprovement(
                                parseFloat(funnel.control.click_rate),
                                parseFloat(funnel.variant.click_rate)
                            )}% vs Control ({funnel.control.click_rate}%)
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-4 p-6 bg-white border border-slate-200 rounded-xl hover:border-blue-500 hover:shadow-lg transition-all duration-200">
                    <div className="flex items-center justify-center w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex-shrink-0">
                        <Activity size={24} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-slate-600 uppercase tracking-wide mb-2">Today's Events</h3>
                        <div className="text-3xl font-bold text-slate-900 mb-1">
                            {realtimeSummary.today_events || 0}
                        </div>
                        <div className="text-sm text-slate-600">
                            Total: {realtimeSummary.total_events || 0} events
                        </div>
                    </div>
                </div>
            </div>

            {/* Conversion Funnel Comparison */}
            <div className="bg-white border border-slate-200 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-slate-900 mb-6">Conversion Funnel</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div>
                        <h4 className="text-base font-semibold text-slate-700 mb-5">Control Group</h4>
                        <div className="flex items-center gap-3 mb-4">
                            <span className="min-w-[80px] font-medium text-slate-700">Views</span>
                            <span className="min-w-[60px] font-semibold text-slate-900">{funnel.control.views}</span>
                            <div className="flex-1 h-2 bg-slate-100 rounded overflow-hidden">
                                <div className="h-full bg-slate-600" style={{ width: '100%' }}></div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 mb-4">
                            <span className="min-w-[80px] font-medium text-slate-700">Clicks</span>
                            <span className="min-w-[60px] font-semibold text-slate-900">{funnel.control.button_clicks}</span>
                            <div className="flex-1 h-2 bg-slate-100 rounded overflow-hidden">
                                <div 
                                    className="h-full bg-slate-600 transition-all duration-500" 
                                    style={{
                                        width: funnel.control.views > 0
                                            ? `${(funnel.control.button_clicks / funnel.control.views * 100)}%`
                                            : '0%'
                                    }}
                                ></div>
                            </div>
                            <span className="min-w-[50px] text-sm font-semibold text-slate-600 text-right">{funnel.control.click_rate}%</span>
                        </div>
                    </div>

                    <div>
                        <h4 className="text-base font-semibold text-slate-700 mb-5">Variant Group</h4>
                        <div className="flex items-center gap-3 mb-4">
                            <span className="min-w-[80px] font-medium text-slate-700">Views</span>
                            <span className="min-w-[60px] font-semibold text-slate-900">{funnel.variant.views}</span>
                            <div className="flex-1 h-2 bg-slate-100 rounded overflow-hidden">
                                <div className="h-full bg-blue-600" style={{ width: '100%' }}></div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 mb-4">
                            <span className="min-w-[80px] font-medium text-slate-700">Clicks</span>
                            <span className="min-w-[60px] font-semibold text-slate-900">{funnel.variant.button_clicks}</span>
                            <div className="flex-1 h-2 bg-slate-100 rounded overflow-hidden">
                                <div 
                                    className="h-full bg-blue-600 transition-all duration-500" 
                                    style={{
                                        width: funnel.variant.views > 0
                                            ? `${(funnel.variant.button_clicks / funnel.variant.views * 100)}%`
                                            : '0%'
                                    }}
                                ></div>
                            </div>
                            <span className="min-w-[50px] text-sm font-semibold text-slate-600 text-right">{funnel.variant.click_rate}%</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Element Test Specific Data */}
            {(test?.basicInfo?.testType === 'element' || metrics?.some(m => m.testType === 'element')) && (
                <div className="bg-white border border-slate-200 rounded-xl p-6">
                    <h3 className="text-xl font-semibold text-slate-900 mb-6">Element Test Details</h3>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div>
                            <h4 className="text-base font-semibold text-slate-700 mb-4">Button Configuration</h4>
                            <div className="flex justify-between items-center py-2 border-b border-slate-100">
                                <span className="font-medium text-slate-600">Button Name:</span>
                                <span className="text-slate-900">{test?.variants?.selectedButton?.name ||
                                    metrics[0]?.metadata?.buttonName || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b border-slate-100">
                                <span className="font-medium text-slate-600">Selector:</span>
                                <code className="font-mono text-xs bg-slate-100 px-2 py-1 rounded border border-slate-200 text-slate-700">
                                    {test?.variants?.selectedButton?.selector ||
                                        metrics[0]?.metadata?.buttonSelector || 'N/A'}
                                </code>
                            </div>
                            <div className="flex justify-between items-center py-2">
                                <span className="font-medium text-slate-600">Test Page:</span>
                                <span className="text-slate-900 text-sm truncate max-w-[200px]">
                                    {test?.variants?.selectedButton?.pageUrl ||
                                        metrics[0]?.metadata?.pageUrl || 'N/A'}
                                </span>
                            </div>
                        </div>

                        <div>
                            <h4 className="text-base font-semibold text-slate-700 mb-4">Position Modification</h4>
                            <div className="flex justify-between items-center py-2 border-b border-slate-100">
                                <span className="font-medium text-slate-600">Target Container:</span>
                                <code className="font-mono text-xs bg-slate-100 px-2 py-1 rounded border border-slate-200 text-slate-700">
                                    {test?.variants?.modifications?.position?.test?.targetSelector ||
                                        metrics[0]?.metadata?.targetSelector || 'N/A'}
                                </code>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b border-slate-100">
                                <span className="font-medium text-slate-600">Position Method:</span>
                                <span className="bg-emerald-500 text-white px-3 py-1 rounded-full text-xs font-semibold capitalize">
                                    {test?.variants?.modifications?.position?.test?.position ||
                                        metrics[0]?.metadata?.positionMethod || 'N/A'}
                                </span>
                            </div>
                            <div className="flex justify-between items-center py-2">
                                <span className="font-medium text-slate-600">Applied Events:</span>
                                <span className="text-slate-900 font-semibold">
                                    {metrics.filter(m => m.event === 'element_applied').length || 0}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    const renderMetricsTab = () => (
        <div className="flex flex-col gap-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h3 className="text-xl font-semibold text-slate-900">Detailed Metrics</h3>
                <select
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                    <option value="7d">Last 7 Days</option>
                    <option value="30d">Last 30 Days</option>
                </select>
            </div>

            {/* Daily Metrics Chart Data */}
            <div className="bg-white border border-slate-200 rounded-xl p-6">
                <h4 className="text-base font-semibold text-slate-700 mb-5">Daily Performance</h4>
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                        <thead className="bg-slate-50">
                            <tr>
                                <th className="px-3 py-3 text-left text-sm font-semibold text-slate-700 border-b border-slate-200">Date</th>
                                <th className="px-3 py-3 text-left text-sm font-semibold text-slate-700 border-b border-slate-200">Control Views</th>
                                <th className="px-3 py-3 text-left text-sm font-semibold text-slate-700 border-b border-slate-200">Control Clicks</th>
                                <th className="px-3 py-3 text-left text-sm font-semibold text-slate-700 border-b border-slate-200">Variant Views</th>
                                <th className="px-3 py-3 text-left text-sm font-semibold text-slate-700 border-b border-slate-200">Variant Clicks</th>
                                <th className="px-3 py-3 text-left text-sm font-semibold text-slate-700 border-b border-slate-200">Improvement</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(dailyMetrics).map(([date, data]) => {
                                const controlRate = data.control.views > 0 ? (data.control.clicks / data.control.views * 100) : 0;
                                const variantRate = data.variant.views > 0 ? (data.variant.clicks / data.variant.views * 100) : 0;
                                const improvement = controlRate > 0 ? ((variantRate - controlRate) / controlRate * 100) : 0;

                                return (
                                    <tr key={date} className="border-b border-slate-100">
                                        <td className="px-3 py-3 text-sm text-slate-900">{new Date(date).toLocaleDateString()}</td>
                                        <td className="px-3 py-3 text-sm text-slate-900">{data.control.views}</td>
                                        <td className="px-3 py-3 text-sm text-slate-900">{data.control.clicks}</td>
                                        <td className="px-3 py-3 text-sm text-slate-900">{data.variant.views}</td>
                                        <td className="px-3 py-3 text-sm text-slate-900">{data.variant.clicks}</td>
                                        <td className={`px-3 py-3 text-sm font-semibold ${improvement >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                            {improvement.toFixed(1)}%
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Raw Metrics Data */}
            <div className="bg-white border border-slate-200 rounded-xl p-6">
                <h4 className="text-base font-semibold text-slate-700 mb-5">Recent Events ({metrics.length} total)</h4>
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                        <thead className="bg-slate-50">
                            <tr>
                                <th className="px-3 py-3 text-left text-sm font-semibold text-slate-700 border-b border-slate-200">Timestamp</th>
                                <th className="px-3 py-3 text-left text-sm font-semibold text-slate-700 border-b border-slate-200">Group</th>
                                <th className="px-3 py-3 text-left text-sm font-semibold text-slate-700 border-b border-slate-200">Event</th>
                                <th className="px-3 py-3 text-left text-sm font-semibold text-slate-700 border-b border-slate-200">URL</th>
                                <th className="px-3 py-3 text-left text-sm font-semibold text-slate-700 border-b border-slate-200">Session ID</th>
                            </tr>
                        </thead>
                        <tbody>
                            {metrics.slice(0, 50).map((metric, index) => (
                                <tr key={index} className="border-b border-slate-100">
                                    <td className="px-3 py-3 text-sm text-slate-900">{new Date(metric.timestamp).toLocaleString()}</td>
                                    <td className="px-3 py-3">
                                        <span className={`px-2 py-1 rounded-xl text-xs font-semibold capitalize ${
                                            metric.group === 'control' 
                                                ? 'bg-slate-100 text-slate-700' 
                                                : 'bg-blue-100 text-blue-800'
                                        }`}>
                                            {metric.group}
                                        </span>
                                    </td>
                                    <td className="px-3 py-3">
                                        <span className="bg-green-50 text-green-800 px-2 py-1 rounded-xl text-xs font-medium">
                                            {metric.event}
                                        </span>
                                    </td>
                                    <td className="px-3 py-3 font-mono text-xs text-slate-600">
                                        {new URL(metric.url).pathname}
                                    </td>
                                    <td className="px-3 py-3 font-mono text-xs text-slate-600">
                                        {metric.sessionId.slice(-8)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    return (
        <div className="mx-auto p-4 md:p-6 bg-white rounded-xl shadow-sm">
            {/* Tab Navigation */}
            <div className="flex border-b-2 border-slate-100 mb-8">
                <button
                    onClick={() => setActiveTab('overview')}
                    className={`flex items-center gap-2 px-4 md:px-5 py-3 font-medium transition-all duration-200 border-b-2 ${
                        activeTab === 'overview'
                            ? 'text-blue-600 border-blue-600 bg-slate-50'
                            : 'text-slate-600 border-transparent hover:text-blue-600 hover:bg-slate-50'
                    }`}
                >
                    <TrendingUp size={16} />
                    Overview
                </button>
                <button
                    onClick={() => setActiveTab('metrics')}
                    className={`flex items-center gap-2 px-4 md:px-5 py-3 font-medium transition-all duration-200 border-b-2 ${
                        activeTab === 'metrics'
                            ? 'text-blue-600 border-blue-600 bg-slate-50'
                            : 'text-slate-600 border-transparent hover:text-blue-600 hover:bg-slate-50'
                    }`}
                >
                    <Activity size={16} />
                    Detailed Metrics
                </button>
            </div>

            {/* Tab Content */}
            <div className="relative">
                {loading && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/90 z-10">
                        <Activity className="w-8 h-8 mb-3 animate-spin text-blue-600" />
                        <span className="text-slate-600">Loading metrics...</span>
                    </div>
                )}

                {error && (
                    <div className="text-center py-8 text-red-600">
                        <p className="mb-3">Error loading metrics: {error}</p>
                        <button 
                            onClick={handleRefreshMetrics} 
                            className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
                        >
                            Retry
                        </button>
                    </div>
                )}

                {activeTab === 'overview' && renderOverviewTab()}
                {activeTab === 'metrics' && renderMetricsTab()}
            </div>
        </div>
    );
};

export default TestSummaryWithMetrics;